// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Deal } from '@interfaces/deal.interfaces';
import { Assignees, DdjDocument, DocumentStatus, FormDocumentUploadType } from '@interfaces/document.interfaces';
import { AppEvent } from '@interfaces/interfaces';
import { User } from '@interfaces/user.interfaces';
import { DealsService } from '@services/deals.service';
import { DocumentsService } from '@services/documents.service';
import { ErrorService } from '@services/error.service';
import { UserService } from '@services/user.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { catchError, firstValueFrom, of, takeWhile } from 'rxjs';
import { formDataToJson } from '../../../utils/formDataToJson';
import { VendorService } from '@services/vendor.service';
import { isMobileOrTablet } from 'src/app/utils/utils';
import { isIdentification } from 'src/app/utils/document-utils';
import { EventsService } from '@services/events.service';

@Component({
    selector: 'app-buyer-sign',
    templateUrl: './buyer-sign.component.html',
    styleUrls: ['./buyer-sign.component.scss'],
})
export class BuyerSignComponent implements OnInit, AfterViewInit, OnDestroy {
    public deal: Deal = null;
    public user: User;
    public dealId: number;
    public docId: number;
    public document: DdjDocument;
    public assignee: Assignees;
    public isComponentAlive = true;
    public isLoading = false;
    public mobileOrTablet = !this.deviceService.isDesktop();
    public fileNameToSave: string;
    public template = 'sign';
    public allowGuidedSigning = false;
    public documentError: any = null;
    private _documentEditInProgress = false;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly dealsService: DealsService,
        private readonly deviceService: DeviceDetectorService,
        private readonly documentsService: DocumentsService,
        private readonly errorService: ErrorService,
        private readonly eventService: EventsService,
        private readonly userService: UserService,
        private readonly vendorService: VendorService
    ) {
        this.route.params.subscribe(async (param) => {
            this.docId = parseInt(param.docId);
            this.dealId = parseInt(param.dealId);

            const document = this.documentsService.getDocById(this.docId);

            if (document?.fileName) {
                this.document = document;
                this.userService.setUserCurrentDealer(this.document.dealerId);
            } else {
                // Retrieve document from elsewhere
                try {
                    const r1Response = await firstValueFrom(this.vendorService.routeOneGetDocument(document.dealerId, document.dealId, document.documentId));

                    if (r1Response?.data?.vendorPartyDocumentFile) {
                        this.document = r1Response.data;
                    }

                    this.userService.setUserCurrentDealer(r1Response.data.dealerId);
                } catch (error) {
                    this.documentError = error;
                }
            }

            this.fileNameToSave = this.document?.fileName;

            if (this.document && this.document.isClosed) {
                this.allowGuidedSigning = false;
            } else {
                this.allowGuidedSigning = true;
            }
        });
    }

    ngOnInit(): void {
        const data = this.route.snapshot.data;
        this.user = data.user;

        data.deal.pipe(takeWhile(() => this.isComponentAlive)).subscribe((deal) => {
            this.deal = deal;
            // Make sure this check happens after the deal is set
            this.assignee = this.deal?.coBuyer?.userId === this.user.userId ? Assignees.cobuyer : Assignees.buyer;
        });
    }

    ngAfterViewInit(): void {
        this.isComponentAlive = true;
        this.eventService.build().then(async () => {
            await this.eventService.subscribeToDeal(this.dealId);
            this.eventService.onDocumentQueueUpdate((update) => {
                if (!this._documentEditInProgress && update.documentId == this.docId) {
                    console.log(
                        `The currently open document: ${update.documentId} has been modified by an external session (another user, or browser).  Cancelling signing session...`
                    );
                    this.errorService
                        .showErrorAtService({
                            title: 'Document Has Been Modified',
                            message: 'This document has been modified by another user, and must be refreshed before it can be modified again.',
                        })
                        .afterClosed()
                        .subscribe(() => {
                            this.cancelSign();
                        });
                }
            });
        });
    }

    ngOnDestroy(): void {
        this.isComponentAlive = false;
        this._documentEditInProgress = false;
        this.eventService.offDocumentQueueUpdate();
    }

    public cancelSign(): void {
        this.router.navigate(['main', 'vehicles', this.dealId]);
    }

    public eventHandler(event: AppEvent): void {
        switch (event.context) {
            case 'sign':
                this.submit(event.data);
                break;
            case 'cancel':
                this.cancelSign();
                break;
            case 'loading':
                this.isLoading = event.data.loading;
                break;
            default:
                break;
        }
    }

    public async getAllDocs() {
        const docs = await firstValueFrom(this.documentsService.getDocsList$(this.deal.dealId));
        return docs.filter(
            (doc) =>
                (doc.documentStatusId === DocumentStatus.DealerQueue ||
                    doc.documentStatusId === DocumentStatus.BuyerQueue ||
                    doc.documentStatusId === DocumentStatus.CoBuyerQueue) &&
                (doc.readyForSignatures || doc.uploadTypeId === FormDocumentUploadType.Uploaded_During_Deal)
        );
    }

    public getCustomerDocs(docs: DdjDocument[]) {
        const isBuyer = this.deal.buyer.userId === this.user.userId;

        if (isBuyer) {
            return docs.filter(
                (doc) =>
                    doc.documentStatusId === DocumentStatus.BuyerQueue &&
                    (doc.readyForSignatures || doc.uploadTypeId === FormDocumentUploadType.Uploaded_During_Deal)
            );
        } else {
            return docs.filter(
                (doc) =>
                    doc.documentStatusId === DocumentStatus.CoBuyerQueue &&
                    (doc.readyForSignatures || doc.uploadTypeId === FormDocumentUploadType.Uploaded_During_Deal)
            );
        }
    }

    private submit(data: any): void {
        if (data?.error) {
            this.router.navigate(['/main', 'vehicles', this.deal.dealId]);
            return;
        }

        this.isLoading = true;
        this._documentEditInProgress = true;

        if (this.vendorService.isDocumentForRouteOne(this.document)) {
            this.vendorService
                .routeOneApplySignatures(formDataToJson(data.formData, false))
                .pipe(
                    takeWhile(() => this.isComponentAlive),
                    catchError((error) => {
                        this.isLoading = false;
                        const errorMessage =
                            error.error?.message === undefined || error.error?.message === null ? 'Could not update the document.' : error.error.message;
                        this.errorService.showError({
                            message: errorMessage,
                            statusCode: error.error?.statusCode,
                        });
                        console.error('error when saving document.', error);
                        throw error;
                    })
                )
                .subscribe((result) => {
                    this.submitUploadSubscribe(result);
                    this.isLoading = false;
                });
        } else if (data.apiSigningEnabled) {
            this.documentsService
                .addSignatures$(this.dealId, this.docId, data.formData, data.fileData, formDataToJson(data.formData))
                .pipe(
                    takeWhile(() => this.isComponentAlive),
                    catchError((error) => {
                        this.isLoading = false;
                        if (!error?.error?.message) {
                            this.errorService.showGenericError();
                            throw error;
                        } else {
                            const errorResponse = this.errorService.showErrorAtService(error.error.message);
                            if (errorResponse) {
                                errorResponse.afterClosed().subscribe(() => {
                                    location.reload();
                                    this.isLoading = false;
                                });
                            }

                            return of(null);
                        }
                    })
                )
                .subscribe((result) => {
                    if (result) {
                        this.submitUploadSubscribe(result, data.routeToNext);
                        this.isLoading = false;
                    }
                });
        }
        //else {
        //    //TODO:DDJ-2369 08-31-2023 Needs to be removed as no longer needed and still calls nodejs
        //    //Left in as PDDJ code needs to be removed which is obsolete
        //    this.documentsService
        //        .uploadDocumentFile$(this.dealId, this.docId, data.formData, data.fileData)
        //        .pipe(
        //            takeWhile(() => (this.isComponentAlive = true)),
        //            catchError((error) => {
        //                this.isLoading = false;
        //                this.errorService.showError('Could not update the document.');
        //                console.error('error when saving document.', error);
        //                throw error;
        //            })
        //        )
        //        .subscribe((result) => {
        //            this.submitUploadSubscribe(result, data.routeToNext);
        //            this.isLoading = false;
        //        });
        //}
    }

    async submitUploadSubscribe(result: any, routeToNext = true) {
        if (!this.deal) {
            this.deal = await firstValueFrom(this.dealsService.getDeal$(this.dealId));
        }

        // Get all documents to check if all documents have been completed
        let docs = await this.getAllDocs();
        const finalizeDocumentsEnabled = this.documentsService.finalizeEnabledForCustomer(this.assignee);

        // Since we enable finalize for customers before all documents are completed,
        // we are no longer going to try and close the deal when customer completes
        let routeToGo = ['/main', 'vehicles', this.deal.dealId];
        docs = this.getCustomerDocs(docs) ?? [];

        // Take the user to the next document if possible
        if (routeToNext) {
            if (this.deal.dealDocs) {
                // Do not go to finalize docs if the user is not ready to yet
                if (!finalizeDocumentsEnabled) {
                    // Docs !== BuyerFinalSignatureDocument(25) AND CoBuyerFinalSignatureDocument(34)
                    docs = docs.filter((d) => d.internalDocumentTypeId !== 25 && d.internalDocumentTypeId !== 34);
                }

                //Do not go to an identification doc if we're not on mobile
                if (!isMobileOrTablet()) {
                    docs = docs.filter((d) => !isIdentification(d.internalDocumentTypeId));
                }

                if (docs.length > 0) {
                    const nextDoc = docs[0];

                    if (nextDoc?.fileName || this.vendorService.isDocumentForRouteOne(nextDoc)) {
                        routeToGo = ['/main', 'vehicles', nextDoc.dealId, nextDoc.documentId, 'sign'];
                    } else {
                        const url = isIdentification(nextDoc.internalDocumentTypeId) ? 'uploadIdn' : 'upload';
                        routeToGo = ['/main', 'vehicles', nextDoc.dealId, nextDoc.documentId, url];
                    }
                }
            }
        }

        this.router.navigate(routeToGo);
        this.isLoading = false;
    }
}
